import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { ScrollText, User, Clock, Filter, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);

  useEffect(() => { fetchLogs(); }, [offset]);
  const fetchLogs = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API_URL}/api/admin/audit-logs?limit=50&offset=${offset}`, { withCredentials: true }); setLogs(data); }
    catch { toast.error('Failed to load audit logs'); }
    setLoading(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-logs-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Audit Logs</h1>
            <p className="text-muted-foreground mt-1">Track all actions across your organization</p>
          </div>
          <Button variant="outline" onClick={fetchLogs} className="border-border text-foreground gap-2"><RefreshCw className="w-4 h-4" />Refresh</Button>
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 glass-card rounded-xl border border-border">
            <ScrollText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground text-lg">No audit logs yet</p>
            <p className="text-sm text-muted-foreground mt-1">Actions performed in the system will appear here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((log, i) => (
              <Card key={i} className="glass-card border-border" data-testid={`audit-log-${i}`}>
                <CardContent className="p-4 flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-muted"><User className="w-4 h-4 text-muted-foreground" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-foreground font-medium text-sm">{log.user_name}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">{log.user_role}</span>
                    </div>
                    <p className="text-sm text-foreground mt-1">{log.action} <span className="text-muted-foreground">on {log.resource_type}</span></p>
                    {log.details && <p className="text-xs text-muted-foreground mt-1">{log.details}</p>}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground flex-shrink-0">
                    <Clock className="w-3 h-3" />
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}
                  </div>
                </CardContent>
              </Card>
            ))}
            <div className="flex justify-center gap-3 pt-4">
              {offset > 0 && <Button variant="outline" onClick={() => setOffset(Math.max(0, offset - 50))} className="border-border text-foreground">Previous</Button>}
              {logs.length === 50 && <Button variant="outline" onClick={() => setOffset(offset + 50)} className="border-border text-foreground">Next</Button>}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
